// src/components/desk/DealEditor.jsx — ONE editor for a card's deal paperwork
// ----------------------------------------------------------------------------
// Owner ruling 2026-07-29/30: when an agent fills the cooperation they usually
// fill the owner's terms too — so 🤝＋ and 🏠＋ open the SAME two-tab editor,
// from the lineage modal AND from card faces. Both bodies stay MOUNTED while it
// is open (hidden, never unmounted) so switching tabs cannot lose typed input;
// saving the cooperation auto-fronts the terms tab once; cancel closes.
// The hosted forms are the real CooperationModal / RentalTermsModal in `inline`
// mode — one implementation per form, this file owns only the chrome.

import { useEffect, useRef, useState } from 'react'
import { t } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { Modal } from './primitives.jsx'
import { CooperationModal } from './CooperationModal.jsx'
import { RentalTermsModal } from './ManualPropertyModal.jsx'
import { ShareTab } from './ShareTab.jsx'
import { ContactPill } from './ContactPill.jsx'
import { CardAnalysis } from './CardAnalysis.jsx'
import { getAuthorRoleMarks } from '../../db/core.js'

// Segmented control with a sliding thumb (2026-07-30, owner: "fancier than pills").
// One moving background element, translated by whole columns — buttons sit above it.
// Deliberately dependency-free: a transform transition is all the animation needs.
export function SegmentedTabs({ value, onChange, options }) {
  const idx = Math.max(0, options.findIndex(([k]) => k === value))
  return (
    <div className="relative mb-4 grid rounded-xl border border-border bg-muted/50 p-1 text-xs font-medium select-none"
      style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0,1fr))` }}>
      <span aria-hidden className="absolute top-1 bottom-1 left-1 rounded-lg bg-background border border-border/60 shadow-sm transition-transform duration-200 ease-out"
        style={{ width: `calc((100% - 0.5rem) / ${options.length})`, transform: `translateX(${idx * 100}%)` }} />
      {options.map(([k, l]) => (
        <button key={k} type="button" onClick={() => onChange(k)}
          /* ⚠️ min-w-0 + overflow-wrap:anywhere (2026-10-09, phone screenshot): four equal columns
             on a ~375px screen are narrower than the one-word label "თანამშრომლობა", and a grid
             item never shrinks below its longest word by default, so it painted OVER the next
             tab. Now an over-long word wraps inside its own tab; desktop widths are unaffected. */
          className={`relative z-10 min-w-0 px-1 sm:px-3 py-1.5 text-center leading-tight [overflow-wrap:anywhere] rounded-lg transition-colors duration-150 ${
            value === k ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
          {l}
        </button>
      ))}
    </div>
  )
}

// coop  = CooperationModal props (propertyId, propertyLabel?, cooperationId?,
//         contactPartyId?, contactName?, seedIntent?, seedCurrency?, seedPrice?, listingId?) | null
// terms = { listingId, mode, propertyId?, rent?, rentCurrency? } | null
// share = { listingId, originListingId? } | null — the reshare handover (B244). Third tab,
//         own component,
//         same inline rule as the other two: this file owns the chrome and nothing else.
//         ⚠️ Unlike coop/terms it is NOT kept mounted while hidden — mounting it fires the
//         payload fetch, which runs AI server-side and can cost a Gemini call. An agent who
//         opens the cooperation tab and never looks at Share must not pay for one.
// Strip a url down to something readable in a chip. Not a security boundary — the href is
// the real thing; this only stops a 120-char portal url from wrapping over three lines.
function urlLabel(u) {
  try { const h = new URL(u).hostname.replace(/^www\./, ''); return h } catch { return u }
}

// sourceUrl (owner request 2026-08-01: "add links to modals too, sometimes it will be useful
// to reload listing") — the ad this editor is about, one click away from every tab. Opening
// the portal page is also how an agent gets the listing RE-SCRAPED: the extension scrapes on
// the page, so "open the source" is the practical way to refresh what core knows when the
// data in here looks stale or wrong.
export function DealEditor({ coop, terms, share = null, initialTab = 'coop', agentId = null,
  sourceUrl = null, authorListing = null, propertyId = null, onClose, onCoopSaved = null, onTermsRecorded = null,
  onTabChange = null }) {
  const [tab, setTab] = useState(initialTab)
  // "Is this ad's author an agent?" (0374). Here it is a PRECONDITION, not a new capture
  // surface: reposting a broker's ad is a different act from reposting an owner's, and the
  // agent should see which one they are about to do. The correction door is the same
  // ContactPill the card face and the lineage modal use — three doors, ONE recorder, so the
  // three surfaces can never disagree about what was reported.
  const [authorMark, setAuthorMark] = useState(null)
  const authorPid = authorListing?.property_id || null
  useEffect(() => {
    if (!authorPid) return
    let gone = false
    getAuthorRoleMarks([authorPid]).then(m => { if (!gone) setAuthorMark(m.get(authorPid) || null) }).catch(() => {})
    return () => { gone = true }
  }, [authorPid])
  // ⚠️ useState(initialTab) reads the prop ONCE, on mount. When this editor is ALREADY open
  // (say on Owner terms) and the agent then presses a different door — ↗ → Repost, or 🤝＋ —
  // the parent recomputes initialTab but the component does not remount, so the tab silently
  // did not change and the door appeared dead (owner-reported 2026-07-31: "when you open
  // reshare to platforms and switch tabs it don't open"). Following the prop makes a door
  // press mean the same thing whether or not the editor happened to be open already.
  useEffect(() => { setTab(initialTab) }, [initialTab])
  // Which tab is FRONTED, reported up (2026-08-05) so a caller that puts it in the URL puts
  // the real one there — `initialTab` is the door that was pressed, and stops being the
  // truth the moment the agent clicks a different tab. Loop-safe with the sync above: the
  // parent echoes this value straight back as initialTab, and setTab to the same value is a
  // no-op. Callers that do not care pass nothing.
  useEffect(() => { if (onTabChange) onTabChange(tab) }, [tab])   // eslint-disable-line react-hooks/exhaustive-deps
  const savedRef = useRef({ coop: false, terms: false })
  // Live view of the coop tab's form (2026-08-01): `dirty` drives the save-before-reshare
  // guard; `price`/`currency` feed the Share tab's "agreed price" hint. The API ref holds
  // the coop form's own save — one implementation, called from two doors.
  const coopApiRef = useRef(null)
  const [coopState, setCoopState] = useState(null)
  // ქონების შეფასება FIRST (owner 2026-09-12: "we add tab here, in front"): the deterministic
  // analysis — statistics, traits, the most-alike flats and the questions for the call — is what
  // the agent reads BEFORE recording anything, so it fronts the paperwork tabs. Needs a property;
  // a manual card without one keeps the old three.
  const valuationPid = propertyId || terms?.propertyId || coop?.propertyId || authorListing?.property_id || null
  const tabs = []
  if (valuationPid) tabs.push(['valuation', t('desk_deal_tab_valuation')])
  tabs.push(['coop', t('desk_coop_modal_title')], ['terms', t('desk_deal_tab_terms')])
  if (share) tabs.push(['share', t('desk_deal_tab_share')])
  return (
    <Modal title={t('desk_deal_editor_title')} onClose={onClose}>
      {/* Above the tabs, so it is reachable from all three rather than living inside one. */}
      {sourceUrl && (
        <a href={sourceUrl} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline mb-2 max-w-full">
          <Icon name="external-link" />
          <span className="truncate">{t('desk_deal_open_source').replace('{h}', urlLabel(sourceUrl))}</span>
        </a>
      )}
      {/* Above the tabs for the same reason the source link is: it qualifies all three,
          and it is the thing to know BEFORE pressing repost. Reads as a muted note, not a
          block — a marked author is a fact about the ad, not a refusal to share it. */}
      {authorPid && (
        <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground">
          <ContactPill listing={authorListing} chip iconOnly agentMark={authorMark}
            /* ⚠️ NOT `desk_author_role_q`: that string became the opener of a sentence its
               two buttons finish ("this contact is: an agent / the owner", 2026-09-09), so it
               reads as a dangling colon anywhere it stands alone. This slot needs a STATE. */
            label={t('desk_author_role_unknown')} />
          <span className="min-w-0 truncate">
            {authorMark?.reported_agent > 0
              ? `${t('desk_author_role_face_tooltip')}${authorMark.reported_agent > 1 ? ` · ${authorMark.reported_agent}` : ''}${authorMark.roster_agency ? ` · ${authorMark.roster_agency}` : ''}`
              : authorMark?.declared === 'agent' ? t('desk_author_role_declared_agent')
              : authorMark?.declared === 'owner' ? t('desk_author_role_declared_owner')
              : t('desk_author_role_unknown')}
          </span>
        </div>
      )}
      <SegmentedTabs value={tab} onChange={setTab} options={tabs} />
      {/* Mounted only while fronted: one SQL round trip per open, none for an agent who came
          for the paperwork. Remounting on return re-fetches — an answer recorded meanwhile
          must show its effect, and the call is cheap (no model). */}
      {valuationPid && tab === 'valuation' && (
        <CardAnalysis propertyId={valuationPid} agentId={agentId} />
      )}
      <div className={tab === 'coop' ? '' : 'hidden'}>
        {coop ? (
          <CooperationModal inline {...coop}
            registerApi={api => { coopApiRef.current = api }}
            onStateChange={setCoopState}
            onSaved={() => { savedRef.current.coop = true; onCoopSaved?.() }}
            onClose={() => {
              // Save flows through onSaved→onClose: front the other half of the
              // paperwork once instead of closing. A plain cancel closes the editor.
              if (savedRef.current.coop && terms && !savedRef.current.terms) setTab('terms')
              else onClose?.()
            }} />
        ) : <p className="text-sm text-muted-foreground">{t('desk_lineage_no_coop')}</p>}
      </div>
      <div className={tab === 'terms' ? '' : 'hidden'}>
        {terms ? (
          <RentalTermsModal inline listingId={terms.listingId} propertyId={terms.propertyId || null}
            mode={terms.mode || 'rent'} agentId={agentId}
            rent={terms.rent ?? null} rentCurrency={terms.rentCurrency ?? null}
            onRecorded={msg => { savedRef.current.terms = true; onTermsRecorded?.(msg) }}
            onClose={onClose} />
        ) : <p className="text-sm text-muted-foreground">{t('desk_shop_terms_none')}</p>}
      </div>
      {/* Mounted only while fronted — see the ⚠️ on `share` above. */}
      {share && tab === 'share' && (
        <ShareTab listingId={share.listingId} originListingId={share.originListingId || null}
          agentId={agentId} onClose={onClose}
          agreedPrice={coopState?.price ?? null} agreedCurrency={coopState?.currency ?? null}
          onBeforeRun={async () => {
            // Flush unsaved cooperation terms before the repost fires (owner ruling
            // 2026-08-01: "any press on a portal button must be doing save"). Returns
            // true when something was actually saved so ShareTab can say so.
            if (!coopState?.dirty || !coopApiRef.current) return false
            // JOIN is a membership change, not a field write — never do it as a side
            // effect of pressing "Korter". The agent is told to press Save themselves
            // (the coop tab's banner already explains that saving joins them).
            if (coopState.joinsOnSave) throw new Error(t('desk_share_save_coop_first'))
            await coopApiRef.current.save({ silent: true })
            savedRef.current.coop = true
            return true
          }} />
      )}
    </Modal>
  )
}
